import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isNativeShell } from '@/lib/shell';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import billingCopy from '@/i18n/ns/billing';
import { useIntlLocale } from '@/workspace/intl-locale';
import type { BillingStatus } from './api';
import { ChangePlanDialog } from './change-plan';
import { useBilling } from './context';
import { clockTime, credits, currentPlanIndex, shortDate, sortPlans } from './plan-math';
import { PAYMENT_FAILED } from './plan-summary';
import type { BillingCopy } from './plan-view';

type PausedText = { title: string; body: string; action: string | null };

function noPlanText(status: BillingStatus, copy: BillingCopy, owner: boolean): PausedText {
  if (PAYMENT_FAILED.has(status.status))
    return {
      title: copy.pausedPlanTitle,
      body: owner ? copy.pausedPastDue : copy.pausedPastDueMember,
      action: null,
    };
  return {
    title: copy.pausedPlanTitle,
    body: owner ? copy.pausedPlanOwner : copy.pausedPlanMember,
    action: owner ? copy.choosePlan : null,
  };
}

function exhaustedText(
  status: BillingStatus,
  copy: BillingCopy,
  locale: string,
  owner: boolean,
): PausedText {
  const account = status.account;
  const plans = sortPlans(status.plans);
  const next =
    plans.find((plan) => plan.id === account.nextPlanId) ??
    plans[currentPlanIndex(plans, account)] ??
    status.assignedPlan;
  const renewal = account.nextPlanAt ?? account.periodEnd;
  const body =
    renewal && next && !account.cancelAtPeriodEnd
      ? formatCopy(owner ? copy.pausedCreditsBody : copy.pausedCreditsMember, {
          date: shortDate(renewal, locale),
          time: clockTime(renewal, locale),
          credits: credits(next.credits, locale),
        })
      : copy.pausedCreditsNoRenewal;
  return { title: copy.pausedCreditsTitle, body, action: owner ? copy.upgradeNow : null };
}

function limitText(status: BillingStatus, copy: BillingCopy, locale: string): PausedText {
  const limit = credits((status.memberLimit?.limitMicros ?? 0) / 1_000_000, locale);
  const renewal = status.account.periodEnd;
  return {
    title: copy.pausedLimitTitle,
    body: renewal
      ? formatCopy(copy.pausedLimitBody, { limit, date: shortDate(renewal, locale) })
      : formatCopy(copy.pausedLimitNoDate, { limit }),
    action: null,
  };
}

/** Why questions are paused, in the words the person can act on. */
export function pausedText(status: BillingStatus, copy: BillingCopy, locale: string): PausedText {
  const owner = status.canManage === true && !isNativeShell();
  if (status.blockedReason === 'member_limit_reached') return limitText(status, copy, locale);
  if (status.blockedReason === 'credits_exhausted')
    return exhaustedText(status, copy, locale, owner);
  return noPlanText(status, copy, owner);
}

/** Opens when a question is refused: no plan, no credits or a personal limit. */
export function BillingDialog({ beforeRedirect }: { beforeRedirect: () => void }) {
  const billing = useBilling();
  const copy = useNamespaceCopy(billingCopy);
  const locale = useIntlLocale();
  const [planOpen, setPlanOpen] = useState(false);
  if (!billing || billing.required === false) return null;
  const status = billing.status;
  const text = status && !status.canAsk ? pausedText(status, copy, locale) : null;
  const pending = billing.returnState === 'pending' || billing.returnState === 'delayed';
  return (
    <>
      <Dialog open={billing.open} onOpenChange={billing.setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{text?.title ?? copy.billingTitle}</DialogTitle>
            <DialogDescription>
              {pending
                ? billing.returnState === 'pending'
                  ? copy.returnPending
                  : copy.returnDelayed
                : (text?.body ?? (billing.error ? copy.unavailable : copy.loading))}
            </DialogDescription>
          </DialogHeader>
          <div className="company-form-actions">
            {text?.action && status && (
              <button
                className="company-primary"
                onClick={() => {
                  billing.setOpen(false);
                  setPlanOpen(true);
                }}
              >
                {text.action}
              </button>
            )}
            {billing.error && (
              <button className="company-secondary" onClick={() => void billing.refresh()}>
                {copy.retry}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {status && (
        <ChangePlanDialog
          open={planOpen}
          onOpenChange={setPlanOpen}
          status={status}
          beforeRedirect={beforeRedirect}
        />
      )}
    </>
  );
}
